'use client';

import useSWR from 'swr';

import { useI18n } from '@fshr/context/i18n-context';
import { defaultFetcher } from '@fshr/shared';
import { cn, getGeoIPCode } from '@fshr/shared-react';

import { Link, Icon, LanguageSelect } from '@fshr/components';
import Navigation from './navigation';
import SignedInLinks from './signed-in-links';
import { useProfile } from '@profiles/lib/context/profile-context';
import { getConfig } from '@fshr/shared-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import throttle from 'lodash/throttle';

type headerProps = {
	subnav: React.ReactNode;
};

export default function Header({ subnav }: headerProps) {
	const { t, localeLink } = useI18n();
	const {
		profile,
		name,
		initials,
		isPersistedAuthenticated,
		isAuthenticated,
		isFullyAuthenticated,
		signOut,
	} = useProfile();
	const { NEXT_PUBLIC_BFF_BASE_URL, NEXT_PUBLIC_WEBSITE_BASE_URL } = getConfig();
	const isEmployee = profile?.isEmployee;
	const ownsProperties = profile?.extension?.propertyOwned || false;
	const geoIpCode = getGeoIPCode();

	const { data, isLoading } = useSWR(
		localeLink(`${NEXT_PUBLIC_BFF_BASE_URL}/header/`),
		defaultFetcher,
		{
			revalidateIfStale: false,
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
		}
	);

	const prevScrollY = useRef(0);
	const prevShowTopBar = useRef(true);
	const [showTopBar, setShowTopBar] = useState(true);
	const throttledSetShowTopBar = throttle(setShowTopBar, 300, { leading: true, trailing: false });
	const onScroll = useCallback(() => {
		const show = window.scrollY < prevScrollY.current;
		throttledSetShowTopBar(show);
		prevShowTopBar.current = show;
		prevScrollY.current = window.scrollY;
	}, []);
	const onScrollEnd = useCallback(() => {
		throttledSetShowTopBar.cancel();
		setShowTopBar(prevShowTopBar.current);
	}, [onScroll]);

	useEffect(() => {
		window.addEventListener('scroll', onScroll);
		window.addEventListener('scrollend', onScrollEnd);

		return () => {
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('scrollend', onScrollEnd);
		};
	}, []);

	return (
		<>
			<div className="hidden lg:block"></div>
			<div
				id="header-nav"
				className={cn(
					'@container/layout fixed w-full top-0 left-0 z-50 header:transition-transform duration-300',
					{
						'header:-translate-y-[25px]': !showTopBar,
						'header:translate-y-0': showTopBar,
					}
				)}
			>
				<header className="bg-neutral-1700 text-text-inverse-heading px-200 header:px-300">
					<div
						className={cn(
							[
								'hidden',
								'h-[25px]',
								'pt-150',
								'header:flex',
								'flex-row',
								'items-center',
								'justify-end',
								'gap-300',
								'header:transition-opacity duration-300 ease-in-out',
							],
							{
								'header:opacity-100': showTopBar,
								'pointer-events-auto': showTopBar,
								'pointer-events-none': !showTopBar,
								'header:opacity-0': !showTopBar,
							}
						)}
					>
						<Link
							href={`${NEXT_PUBLIC_WEBSITE_BASE_URL}${localeLink('/find_a_hotel_or_resort/')}`}
							intent="unstyled"
							prefetch={false}
							className={cn([
								'text-white',
								'text-caption2',
								'hover:text-neutral-900',
								'focus:text-neutral-900',
								'transition-colors',
							])}
							onFocus={() => setShowTopBar(true)}
						>
							{t('All Hotels and Resorts')}
						</Link>

						{!isLoading && isAuthenticated && (
							<SignedInLinks
								isPersisted={isPersistedAuthenticated}
								welcomeName={profile?.name?.firstName || ''}
								initials={initials}
								name={name}
								hasMembership={profile?.hasMembership}
								hasOwnedProperties={ownsProperties}
								hasUpcomingTrips={data?.hasUpcomingTrips}
								signOut={signOut}
								onFocus={() => setShowTopBar(true)}
								topBarShowing={showTopBar}
							/>
						)}

						{!isLoading && !isAuthenticated && (
							<Link
								href={localeLink('/sign-in')}
								intent="unstyled"
								className={cn([
									'font-sans',
									'font-normal',
									'text-[12px]',
									'tracking-[1px]',
									'leading-[18px]',
									'text-white',
									'hover:text-neutral-900',
									'focus:text-neutral-900',
									'transition-colors',
									'!gap-060',
								])}
								icon={<Icon icon="person" className="text-[18px]" />}
								iconPosition="start"
								onFocus={() => setShowTopBar(true)}
							>
								{t('Sign In')}
							</Link>
						)}

						<LanguageSelect onFocus={() => setShowTopBar(true)} className="header:border-t-white" />
					</div>

					<div
						className={cn([
							'h-[60px]',
							'gap-450',
							'flex',
							'flex-row',
							'justify-between',
							'items-center',
							'header:h-[95px]',
							'header:grid',
							'header:grid-cols-[min-content_1fr_min-content]',
						])}
					>
						<Link
							href="/"
							intent="unstyled"
							className={cn([
								'grid',
								'place-content-center',
								'leading-none',
								'text-[40px]',
								'h-[40px]',
								'w-[26px]',
								'header:text-[52px]',
								'header:h-[55px]',
								'header:w-[36px]',
							])}
							prefetch={false}
						>
							<Icon icon="tree" />
							<span className="visually-hidden">{t('Go to the Four Seasons home page')}</span>
						</Link>

						<Navigation
							isSignedIn={isAuthenticated}
							isPersisted={isPersistedAuthenticated}
							isFullyAuthenticated={isFullyAuthenticated}
							name={name}
							initials={initials}
							hasUpcomingTrips={data?.hasUpcomingTrips}
							hasMembership={profile?.hasMembership}
							hasOwnedProperties={ownsProperties}
							isEmployee={isEmployee}
							signOut={signOut}
							navLinks={data?.headerNavigationLinks.filter(
								({ geoIpRestrictions }: any) => !geoIpRestrictions?.includes(geoIpCode)
							)}
							isLoading={isLoading}
						/>
					</div>
				</header>
				{isAuthenticated && subnav}
			</div>
		</>
	);
}
